import React, { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { Calendar, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import AuthorProfile from "@/components/marketing/guides/AuthorProfile";
import GuideToc from "@/components/marketing/guides/GuideToc";
import RichContent from "@/components/marketing/guides/RichContent";
import SocialShareButtons from "@/components/marketing/guides/SocialShareButtons";
import Reveal from "@/components/marketing/Reveal";

const CATEGORY_LABELS = {
  pr_tips: "PR Tips",
  industry_news: "Industry News",
  case_studies: "Case Studies",
  seo: "SEO",
  digital_pr: "Digital PR",
};

/** Category-based fallback when related_service is not set on the post. */
const CATEGORY_SERVICE_MAP = {
  seo: "/services/guaranteed-placements",
  pr_tips: "/services/journalist-outreach",
  digital_pr: "/services/newswire-distribution",
  case_studies: "/packages",
  industry_news: "/packages",
};

const SERVICE_INFO = {
  "/services/guaranteed-placements": {
    label: "Guaranteed Placements",
    blurb:
      "Turn these insights into action with guaranteed placements on indexed, high-authority outlets.",
  },
  "/services/journalist-outreach": {
    label: "Journalist Outreach",
    blurb:
      "Put these PR techniques to work with targeted journalist outreach from our team.",
  },
  "/services/newswire-distribution": {
    label: "Newswire Distribution",
    blurb:
      "Ready to distribute? Reach journalists and outlets across our global newswire network.",
  },
  "/packages": {
    label: "Curated Packages",
    blurb:
      "See how these strategies come together in our curated PR packages.",
  },
};

export default function GuideDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setAuthor(null);
    base44.entities.BlogPost.filter({ is_published: true, slug }, "-published_date", 1)
      .then((res) => {
        const found = res[0] || null;
        setPost(found);
        if (found?.author) {
          base44.entities.Author.filter({ name: found.author }, undefined, 1)
            .then((aRes) => setAuthor(aRes[0] || null))
            .catch(() => setAuthor(null));
        }
      })
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-24 lg:pt-32 min-h-screen flex items-center justify-center bg-canvas">
        <div className="w-6 h-6 border-2 border-cobalt/20 border-t-cobalt rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-24 lg:pt-32 min-h-screen bg-canvas">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
          <p className="font-heading text-2xl font-bold text-bone mb-3">
            Guide Not Found
          </p>
          <p className="text-steel mb-8">
            The guide you're looking for doesn't exist or has been moved.
          </p>
          <Link to="/guides">
            <Button variant="outline" className="border-border text-bone hover:text-bone">
              <ArrowLeft className="mr-2 w-4 h-4" /> Back to Guides
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const servicePath =
    post.related_service || CATEGORY_SERVICE_MAP[post.category] || "/packages";
  const serviceInfo = SERVICE_INFO[servicePath] || SERVICE_INFO["/packages"];

  return (
    <div className="pt-24 lg:pt-32 min-h-screen bg-canvas">
      <Seo
        title={post.title}
        description={post.meta_description || post.excerpt}
        image={post.cover_image}
        imageAlt={post.title}
        url={`https://digital24.com/guides/${post.slug}`}
        articleMeta={{
          published_time: post.published_date,
          modified_time: post.updated_date || post.published_date,
          author: author?.name || post.author,
          section: CATEGORY_LABELS[post.category],
          tags: post.tags,
        }}
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.excerpt,
          image: post.cover_image
            ? [post.cover_image]
            : ["https://media.base44.com/images/public/6a460432963500ce22897197/1af990067_Whitelogo03.png"],
          datePublished: post.published_date,
          dateModified: post.updated_date || post.published_date,
          articleSection: CATEGORY_LABELS[post.category] || undefined,
          keywords: post.tags && post.tags.length > 0 ? post.tags.join(", ") : undefined,
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://digital24.com/guides/${post.slug}`,
          },
          author: author
            ? {
                "@type": "Person",
                name: author.name,
                jobTitle: author.title || undefined,
                description: author.bio || undefined,
                image: author.profile_image_url || undefined,
                url: author.linkedin_url || author.instagram_url || undefined,
                sameAs: [
                  author.linkedin_url,
                  author.instagram_url,
                  author.twitter_url,
                ].filter(Boolean),
              }
            : { "@type": "Organization", name: post.author || "Digital24" },
          publisher: {
            "@type": "Organization",
            name: "Digital24",
            logo: {
              "@type": "ImageObject",
              url: "https://media.base44.com/images/public/6a460432963500ce22897197/1af990067_Whitelogo03.png",
            },
          },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://digital24.com/" },
            { "@type": "ListItem", position: 2, name: "Guides", item: "https://digital24.com/guides" },
            {
              "@type": "ListItem",
              position: 3,
              name: post.title,
              item: `https://digital24.com/guides/${post.slug}`,
            },
          ],
        },
      ]}
      />
      {/* Hero header image */}
      <Reveal>
      {post.cover_image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="aspect-[21/9] overflow-hidden rounded-xl">
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}
      </Reveal>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className={post.category === "case_studies" ? "" : "lg:grid lg:grid-cols-[200px_1fr] lg:gap-12"}>
          {/* Jump nav — left sidebar — hidden for case studies (full-width report) */}
          {post.category !== "case_studies" && (
            <aside className="hidden lg:block">
              <GuideToc contentRef={contentRef} />
            </aside>
          )}

          <article className="min-w-0">
            <Reveal>
            <Link
              to={post.category === "case_studies" ? "/case-studies" : "/guides"}
              className="inline-flex items-center text-steel hover:text-bone text-sm mb-8 transition-colors"
            >
              <ArrowLeft className="mr-2 w-4 h-4" /> {post.category === "case_studies" ? "All Case Studies" : "All Guides"}
            </Link>

            <div className="flex items-center gap-3 mb-4">
              {post.category && (
                <span className="meta-tag text-[9px]">
                  {CATEGORY_LABELS[post.category] || post.category}
                </span>
              )}
              {post.published_date && (
                <span className="font-mono text-[10px] text-steel flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {post.published_date}
                </span>
              )}
            </div>

            <h1 className="font-heading text-3xl lg:text-4xl font-bold text-bone leading-tight mb-4">
              {post.title}
            </h1>
            {post.excerpt && (
              <p className="text-lg text-steel leading-relaxed mb-6">{post.excerpt}</p>
            )}

            {/* Written by — top byline */}
            {author && (
              <div className="flex items-center gap-3 mb-8 pb-6 border-b border-border">
                {author.profile_image_url && (
                  <img
                    src={author.profile_image_url}
                    alt={author.name}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-border"
                  />
                )}
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-steel">
                    Written by
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="font-heading text-bone font-bold text-sm">{author.name}</p>
                    {author.title && (
                      <span className="text-xs text-steel">— {author.title}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
            </Reveal>

            <RichContent
              html={post.content}
              contentRef={contentRef}
              className="rich-content max-w-none text-bone"
            />

            <Reveal>
            <div className="mt-8 pt-6 border-t border-border">
              <SocialShareButtons
                url={`https://digital24.com/guides/${post.slug}`}
                title={post.title}
              />
            </div>
            </Reveal>

            <Reveal>
            {author ? (
              <AuthorProfile author={author} />
            ) : (
              post.author && (
                <div className="mt-12 pt-8 border-t border-border">
                  <p className="font-mono text-xs uppercase tracking-widest text-steel">
                    Written by
                  </p>
                  <p className="font-heading text-bone mt-1">{post.author}</p>
                </div>
              )
            )}
            </Reveal>

            {/* Lead-gen close: quiet related-service block */}
            <Reveal>
            <div className="mt-10 p-6 bg-slate/50 border border-border rounded-lg">
              <p className="text-sm text-steel leading-relaxed mb-3">
                {serviceInfo.blurb}
              </p>
              <Link
                to={servicePath}
                className="inline-flex items-center gap-1 text-cobalt font-medium text-sm hover:underline"
              >
                Explore {serviceInfo.label} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            </Reveal>

            <Reveal>
            <div className="mt-10 text-center">
              <Link to={post.category === "case_studies" ? "/case-studies" : "/guides"}>
                <Button
                  variant="outline"
                  className="border-border text-bone hover:text-bone"
                >
                  <ArrowLeft className="mr-2 w-4 h-4" /> {post.category === "case_studies" ? "Back to all case studies" : "Back to all guides"}
                </Button>
              </Link>
            </div>
            </Reveal>
          </article>
        </div>
      </div>
    </div>
  );
}